/**
 * Fixed side panel (desktop) / drawer content (mobile) for the selected concept.
 * Badge text is passed in so it stays identical to the pill above the node on the map.
 */

import { Box, Button, Typography } from '@mui/material';
import type { LayoutNode } from './knowledgeConcepts.types';
import type { MasteryStatusConfigEntry } from './knowledgeConcepts.status';

export const PANEL_DEFAULT_EMOJI = '🕵🏼‍♀️';
export const MODULE_MASTERED_EMOJI = '🏆';

export interface KnowledgeConceptsPanelProps {
  selectedNode: LayoutNode | undefined;
  cfg: MasteryStatusConfigEntry;
  /** `null` = no navigation pill. */
  badgeLabel: string | null;
  badgeEmoji?: string;
  /** When set, pill uses session nav colors; otherwise falls back to `cfg` badge colors. */
  badgePillStyle?: { badgeBg: string; badgeColor: string } | null;
  handleButtonAction?: () => void;
  /**
   * Session recommends diagnostic and user has no concept selected:
   * hide concept title / secondary name, optional neutral hero, CTA "Start Diagnostic".
   */
  diagnosticDefaultPanel?: boolean;
  moduleName?: string;
  conceptsCount?: number;
  isRecommended?: boolean;
  onRecommendedPathClick?: () => void;
  buttonLabel?: string;
  isModuleMastered?: boolean;
  modulesMasteredCount?: number;
  totalModulesCount?: number;
  moduleProgress?: number;
}

export function KnowledgeConceptsPanel({
  selectedNode,
  cfg,
  badgeLabel,
  badgeEmoji,
  badgePillStyle,
  handleButtonAction,
  diagnosticDefaultPanel = false,
  moduleName,
  conceptsCount,
  isRecommended,
  onRecommendedPathClick,
  buttonLabel,
  isModuleMastered,
  modulesMasteredCount,
  totalModulesCount,
  moduleProgress,
}: KnowledgeConceptsPanelProps) {
  const pillBg = badgePillStyle?.badgeBg ?? cfg.badgeBg;
  const pillColor = badgePillStyle?.badgeColor ?? cfg.badgeColor;
  const primaryBtnLabel = diagnosticDefaultPanel
    ? 'Start Warm up'
    : isModuleMastered
    ? 'View other Chapters'
    : buttonLabel || cfg.btnLabel;
  const panelDesc = isModuleMastered
    ? undefined
    : diagnosticDefaultPanel
    ? `This chapter covers ${conceptsCount || ''} concepts${
        moduleName ? ` in ${moduleName}` : ''
      }. Let's start with a quick warm up to understand where you stand — we'll use your answers to create a personalized learning path.`
    : isRecommended
    ? "This is the concept we recommend tackling next. It builds directly on the concepts you've already worked through, so you're well-prepared to dive in. Working through this will bring you one step closer to completing the chapter."
    : cfg.panelDesc;
  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: { xs: 160, md: 220 },
          background: cfg.illustrationBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          flexShrink: 0,
          fontSize: { xs: 60, md: 80 },
        }}
      >
        {diagnosticDefaultPanel
          ? PANEL_DEFAULT_EMOJI
          : isModuleMastered
          ? MODULE_MASTERED_EMOJI
          : selectedNode?.emoji}
      </Box>
      <Box
        sx={{
          flex: 1,
          p: { xs: '20px 20px 16px', md: '28px 28px 20px' },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
        }}
      >
        {(badgeLabel != null || diagnosticDefaultPanel) && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              background: pillBg,
              color: pillColor,
              px: 1.75,
              py: 1.25,
              borderRadius: 100,
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              boxShadow: `0 4px 16px ${
                cfg.btnShadow.split('rgba')[1]
                  ? 'rgba' + cfg.btnShadow.split('rgba')[1]
                  : 'rgba(0,0,0,0.1)'
              }`,
              alignSelf: 'flex-start',
            }}
          >
            <span style={{ fontSize: 11, lineHeight: 1 }}>
              {badgeEmoji || '📚'}
            </span>
            <span style={{ lineHeight: 1 }}>
              {diagnosticDefaultPanel
                ? "Let's see where you stand"
                : badgeLabel}
            </span>
          </Box>
        )}
        {isModuleMastered && (
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 700,
                color: '#0f172a',
                lineHeight: 1.3,
                letterSpacing: -0.3,
                textAlign: 'center',
              }}
            >
              Chapter Mastered
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.6,
                color: '#64748b',
                mt: '10px',
                textAlign: 'center',
              }}
            >
              You've mastered every concept in {moduleName}. Outstanding work!
            </Typography>
            {(modulesMasteredCount != null || moduleProgress != null) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  mt: '16px',
                }}
              >
                {modulesMasteredCount != null && (
                  <Box
                    sx={{
                      textAlign: 'center',
                      background:
                        'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                      border: '1px solid #34d399',
                      borderRadius: '12px',
                      padding: '10px 18px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: 800,
                        color: '#0f172a',
                        lineHeight: 1.2,
                      }}
                    >
                      {modulesMasteredCount}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: 0.8,
                        mt: '2px',
                      }}
                    >
                      CONCEPTS
                    </Typography>
                  </Box>
                )}
                {moduleProgress != null && (
                  <Box
                    sx={{
                      textAlign: 'center',
                      background:
                        'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                      border: '1px solid #34d399',
                      borderRadius: '12px',
                      padding: '10px 18px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: 800,
                        color: '#0f172a',
                        lineHeight: 1.2,
                      }}
                    >
                      {Math.round(moduleProgress)}%
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: 0.8,
                        mt: '2px',
                      }}
                    >
                      MASTERED
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
        <Typography
          sx={{
            fontSize: { xs: 20, md: 24 },
            fontWeight: 700,
            color: '#0f172a',
            lineHeight: 1.3,
            letterSpacing: -0.3,
          }}
        >
          {diagnosticDefaultPanel
            ? moduleName || 'Take Diagnostic Test'
            : selectedNode?.title}
        </Typography>
        {panelDesc && (
          <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: '#64748b' }}>
            {panelDesc}
          </Typography>
        )}
        {!(diagnosticDefaultPanel || isModuleMastered) && (
          <Box
            sx={{
              p: '12px 14px',
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 2.5,
            }}
          >
            <Typography
              sx={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}
            >
              {selectedNode?.name}
            </Typography>
          </Box>
        )}
        {!isModuleMastered && (
          <Box
            sx={{
              marginTop: '12px',
              padding: '10px 12px',
              background: cfg.labelBg,
              borderLeft: `3px solid ${cfg.labelBorder}`,
              borderRadius: '6px',
              color: cfg.labelColor,
            }}
          >
            <Typography
              sx={{ fontSize: 14, color: cfg.labelColor, lineHeight: 1.5 }}
            >
              {!!(isRecommended || diagnosticDefaultPanel)
                ? `You're also free to click on any ${
                    isRecommended ? 'other ' : ''
                  }concept and start learning right away if you wish.`
                : 'You can also click outside to follow the recommended path'}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          p: { xs: '16px 20px 24px', md: '20px 28px 32px' },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          mt: 'auto',
        }}
      >
        <Button
          fullWidth
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.25,
            py: 2,
            px: 3.5,
            background: cfg.btnBg,
            border: 'none',
            borderRadius: 3.5,
            fontSize: 15,
            fontWeight: 600,
            color: cfg.btnColor,
            textTransform: 'none',
            boxShadow: cfg.btnShadow,
            transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
            '&:hover': {
              background: cfg.btnBg,
              transform: 'translateY(-2px) scale(1.02)',
              boxShadow: cfg.btnShadow.replace(/0\.\d+\)/, '0.45)'),
            },
            '&:disabled': {
              background: '#e2e8f0',
              color: '#94a3b8',
              boxShadow: 'none',
            },
          }}
          onClick={handleButtonAction}
        >
          {primaryBtnLabel}
          <span>→</span>
        </Button>
        {diagnosticDefaultPanel || isModuleMastered || isRecommended ? (
          <Typography
            sx={{
              fontSize: 12,
              color: '#94a3b8',
              textAlign: 'center',
              lineHeight: 1.4,
            }}
          >
            {diagnosticDefaultPanel
              ? 'Answer a few questions so we can personalize your journey'
              : isRecommended || isModuleMastered
              ? 'Click any concept on the map to explore'
              : ''}
          </Typography>
        ) : onRecommendedPathClick ? (
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                color: cfg.labelColor,
                lineHeight: 1.5,
                textDecoration: 'underline',
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={onRecommendedPathClick}
            >
              ← Back to recommended path
            </Typography>
          </Box>
        ) : null}
      </Box>
    </>
  );
}
